import React from "react";
import { ArrowUp, ArrowDown, Trash2 } from "lucide-react";

const TYPES = [
  ["single", "Single choice"], ["multi", "Multiple choice"], ["yesno", "Yes / No"],
  ["text", "Short text"], ["textarea", "Long text"], ["number", "Number"],
];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);
const optsOf = (q) => (q.type === "yesno" ? ["Yes", "No"] : q.options || []);

export default function QuestionEditor({ q, earlier, onChange, onMove, onRemove, readOnly, tid }) {
  const set = (k, v) => onChange({ ...q, [k]: v });
  const dep = earlier.find((e) => e.id === q.show_if?.q);
  const choice = ["single", "multi"].includes(q.type);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid={tid}>
      <div className="flex items-start gap-2">
        <input value={q.label} disabled={readOnly} onChange={(e) => onChange({ ...q, label: e.target.value, id: q._new ? slug(e.target.value) || q.id : q.id })} placeholder="Question text" className="qb-input flex-1 font-medium" data-testid={`${tid}-label`} />
        {!readOnly && (
          <div className="flex gap-1">
            <button type="button" onClick={() => onMove(-1)} className="qb-icon" data-testid={`${tid}-up`}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onMove(1)} className="qb-icon" data-testid={`${tid}-down`}><ArrowDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={onRemove} className="qb-icon text-rose-600" data-testid={`${tid}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <select value={q.type} disabled={readOnly} onChange={(e) => set("type", e.target.value)} className="qb-input" data-testid={`${tid}-type`}>
          {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={q.id} disabled={readOnly || !q._new} onChange={(e) => set("id", slug(e.target.value))} className="qb-input font-mono text-xs" title="Answer key (fixed once saved)" data-testid={`${tid}-key`} />
        <label className="inline-flex items-center gap-2 text-xs px-2"><input type="checkbox" disabled={readOnly} checked={!!q.required} onChange={(e) => set("required", e.target.checked)} data-testid={`${tid}-required`} /> Required</label>
      </div>
      {choice && (
        <textarea rows={3} disabled={readOnly} value={(q.options || []).join("\n")} onChange={(e) => set("options", e.target.value.split("\n"))} placeholder="One option per line" className="qb-input text-sm" data-testid={`${tid}-options`} />
      )}
      <input value={q.help || ""} disabled={readOnly} onChange={(e) => set("help", e.target.value)} placeholder="Helper text (optional)" className="qb-input text-xs" data-testid={`${tid}-help`} />
      <div className="rounded-xl bg-slate-50 p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#252A34]/70">Show only when</span>
          <select value={q.show_if?.q || ""} disabled={readOnly} onChange={(e) => set("show_if", e.target.value ? { q: e.target.value, in: [] } : null)} className="qb-input py-1 text-xs flex-1" data-testid={`${tid}-showif-q`}>
            <option value="">Always show</option>
            {earlier.filter((e) => ["single", "multi", "yesno"].includes(e.type)).map((e) => <option key={e.id} value={e.id}>{e.label || e.id}</option>)}
          </select>
        </div>
        {dep && (
          <div className="flex flex-wrap gap-1.5">
            {optsOf(dep).filter(Boolean).map((o) => {
              const on = (q.show_if.in || []).includes(o);
              return <button type="button" key={o} disabled={readOnly} onClick={() => set("show_if", { ...q.show_if, in: on ? q.show_if.in.filter((x) => x !== o) : [...q.show_if.in, o] })} className={`rounded-full border px-2.5 py-1 text-[11px] ${on ? "bg-[#165a71] text-white border-[#165a71]" : "bg-white border-slate-200"}`} data-testid={`${tid}-showif-opt-${o}`}>{o}</button>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export const QB_STYLES = `
  .qb-input { width: 100%; border-radius: .7rem; border: 1px solid #e2e8f0; background: white; padding: .5rem .7rem; font-size: .85rem; outline: none; }
  .qb-input:focus { border-color: #165a71; box-shadow: 0 0 0 3px rgba(22,90,113,.12); }
  .qb-input:disabled { background: #f8fafc; color: #64748b; }
  .qb-icon { height: 2rem; width: 2rem; border-radius: .6rem; border: 1px solid #e2e8f0; display: inline-flex; align-items: center; justify-content: center; background: white; }
  .qb-icon:hover { background: #f1f5f9; }
`;
